/**
 * Touch controls.
 *
 * Drag anywhere on the floor to walk: the touch point becomes a virtual
 * joystick anchored where your thumb landed, rather than a fixed control in a
 * corner you have to find. Direction is the drag vector, and distance scales
 * speed, so a small drag is a careful step and a big one is a stride.
 *
 * Feeds the same analog input a gamepad would, so movement, collision and
 * server validation are untouched by any of this.
 */

import type { InputController } from './input.js';

/** Drags shorter than this are taps, not movement. */
const DEAD_ZONE = 12;
/** Distance at which you are walking at full speed. */
const FULL_TILT = 56;

export interface TouchControls {
  readonly active: boolean;
  destroy(): void;
}

export function isTouchDevice(): boolean {
  return window.matchMedia('(pointer: coarse)').matches || navigator.maxTouchPoints > 0;
}

export function createTouchControls(
  canvas: HTMLCanvasElement,
  knob: HTMLElement,
  input: () => InputController | null,
): TouchControls {
  let touchId: number | null = null;
  let originX = 0;
  let originY = 0;

  const show = (x: number, y: number, dx: number, dy: number) => {
    const distance = Math.hypot(dx, dy);
    // Keep the knob inside the ring however far the thumb travels.
    const clamped = distance > FULL_TILT ? FULL_TILT / distance : 1;
    knob.style.transform = `translate(${x + dx * clamped}px, ${y + dy * clamped}px)`;
    knob.classList.add('visible');
  };

  const hide = () => {
    knob.classList.remove('visible');
    input()?.setAnalog(0, 0, 0);
  };

  const onStart = (event: TouchEvent) => {
    if (touchId !== null) return;
    const touch = event.changedTouches[0];
    if (!touch) return;
    touchId = touch.identifier;
    originX = touch.clientX;
    originY = touch.clientY;
    show(originX, originY, 0, 0);
    // Stop the page scrolling or triggering pull-to-refresh under the thumb.
    event.preventDefault();
  };

  const find = (list: TouchList): Touch | null => {
    for (let i = 0; i < list.length; i++) {
      const touch = list[i];
      if (touch && touch.identifier === touchId) return touch;
    }
    return null;
  };

  const onMove = (event: TouchEvent) => {
    if (touchId === null) return;
    const touch = find(event.changedTouches);
    if (!touch) return;

    const dx = touch.clientX - originX;
    const dy = touch.clientY - originY;
    show(originX, originY, dx, dy);

    const distance = Math.hypot(dx, dy);
    if (distance < DEAD_ZONE) {
      input()?.setAnalog(0, 0, 0);
    } else {
      input()?.setAnalog(dx, dy, Math.min(1, distance / FULL_TILT));
    }
    event.preventDefault();
  };

  const onEnd = (event: TouchEvent) => {
    if (touchId === null) return;
    if (!find(event.changedTouches)) return;
    touchId = null;
    hide();
  };

  canvas.addEventListener('touchstart', onStart, { passive: false });
  canvas.addEventListener('touchmove', onMove, { passive: false });
  canvas.addEventListener('touchend', onEnd);
  canvas.addEventListener('touchcancel', onEnd);
  // A touch that ends outside the canvas still has to release the joystick.
  window.addEventListener('blur', hide);

  return {
    get active() {
      return touchId !== null;
    },
    destroy() {
      canvas.removeEventListener('touchstart', onStart);
      canvas.removeEventListener('touchmove', onMove);
      canvas.removeEventListener('touchend', onEnd);
      canvas.removeEventListener('touchcancel', onEnd);
      window.removeEventListener('blur', hide);
      hide();
    },
  };
}
